import * as XLSX from 'xlsx';

export interface ArmsoftCodes {
  cash_desk_code: string;
  card_account_code: string;
  vat_income_code: string;
  exempt_income_code: string;
  turnover_income_code: string;
  purchase_account_code: string;
}

export interface BooksPack {
  sales: Array<{
    day: string;
    orders: number;
    gross: number;
    cash: number;
    card: number;
    tips: number;
    fiscal_orders: number;
    missing_fiscal: number;
    missing_fiscal_amount: number;
  }>;
  tax: Array<{
    day: string;
    department_id: number | null;
    department_name: string | null;
    tax_type: number;
    net: number;
    discounts: number;
    cogs: number;
  }>;
  orderLines: Array<{
    day: string;
    order_number: number;
    kind: string;
    fiscal_no: string | null;
    department_id: number | null;
    department_name: string | null;
    tax_type: number;
    line_net: number;
    discount: number;
    cash: number;
    card: number;
    tip: number;
    partner_tin: string | null;
    comp_type: string | null;
    close_reason: string | null;
  }>;
  onHand: Array<{
    ingredient: string;
    unit: string;
    quantity: number;
    unit_cost: number;
    value: number;
  }>;
  stays: Array<{
    day: string;
    room: string;
    guest_name: string;
    check_in: string;
    check_out: string;
    nights: number;
    amount: number;
    payment_option: string;
    payment_status: string;
    payment_method: string | null;
    fiscal_no: string | null;
    source: string;
  }>;
  returns: Array<{
    day: string;
    order_number: number;
    total: number;
    fiscal_no: string | null;
    partner_tin: string | null;
  }>;
  comps: Array<{ day: string; comp_type: string; count: number; amount: number }>;
  voids: Array<{
    day: string;
    order_number: number;
    total: number;
    close_reason: string | null;
    comp_type: string | null;
  }>;
  b2b: Array<{
    day: string;
    order_number: number;
    tin: string;
    total: number;
    fiscal_no: string | null;
  }>;
  purchases: Array<{
    day: string;
    serial_no: string;
    supplier_tin: string;
    supplier_name: string;
    goods_name: string;
    quantity: number;
    unit: string;
    unit_price: number;
    line_total: number;
    invoice_vat: number;
    invoice_total: number;
  }>;
  stock: Array<{
    day: string;
    ingredient: string;
    unit: string;
    system_qty: number;
    counted_qty: number;
    delta: number;
    unit_cost: number;
    value: number;
    note: string | null;
  }>;
  waste: Array<{
    day: string;
    ingredient: string;
    unit: string;
    quantity: number;
    unit_cost: number;
    value: number;
    note: string | null;
  }>;
  drops: Array<{
    day: string;
    expected_amount: number;
    counted_amount: number | null;
    status: string;
    note: string | null;
  }>;
  expenses: Array<{
    day: string;
    category: string;
    amount: number;
    description: string | null;
  }>;
}

export const EMPTY_CODES: ArmsoftCodes = {
  cash_desk_code: '',
  card_account_code: '',
  vat_income_code: '',
  exempt_income_code: '',
  turnover_income_code: '',
  purchase_account_code: '',
};

type Label = (key: string) => string;

function sheet(name: string, headers: string[], rows: Array<Array<string | number | null>>) {
  const safe = name.replace(/[\\/?*[\]:]/g, ' ').slice(0, 31);
  return { name: safe || 'Sheet', sheet: XLSX.utils.aoa_to_sheet([headers, ...rows]) };
}

function incomeCode(codes: ArmsoftCodes, taxType: number): string {
  if (taxType === 1) return codes.vat_income_code;
  if (taxType === 2) return codes.exempt_income_code;
  if (taxType === 3) return codes.turnover_income_code;
  return '';
}

export function taxTypeLabel(t: Label, taxType: number): string {
  if (taxType === 1) return t('books.taxVat');
  if (taxType === 2) return t('books.taxExempt');
  if (taxType === 3) return t('books.taxTurnover');
  return t('books.taxUnknown');
}

export function orderKindLabel(t: Label, kind: string): string {
  if (kind === 'return') return t('books.kindReturn');
  if (kind === 'void') return t('books.kindVoid');
  if (kind === 'comp') return t('books.kindComp');
  return t('books.kindSale');
}

export function stayOptionLabel(t: Label, option: string): string {
  if (option === 'online') return t('bookings.paymentOptionValue.online');
  if (option === 'free') return t('bookings.paymentOptionValue.free');
  return t('bookings.paymentOptionValue.on_site');
}

export function stayStatusLabel(t: Label, status: string): string {
  if (status === 'paid') return t('bookings.paymentStatus.paid');
  if (status === 'pending') return t('bookings.paymentStatus.pending');
  if (status === 'failed') return t('bookings.paymentStatus.failed');
  return t('bookings.paymentStatus.unpaid');
}

export function stayTenderLabel(t: Label, method: string | null): string {
  if (method === 'cash') return t('bookings.tender.cash');
  if (method === 'card') return t('bookings.tender.card');
  return '';
}

/** One workbook, one sheet per ArmSoft document. Headers follow the UI language. */
export function downloadBooksPack(
  pack: BooksPack,
  codes: ArmsoftCodes,
  t: Label,
  filename: string,
) {
  const book = XLSX.utils.book_new();
  const sheets = [
    sheet(
      t('books.sheetSales'),
      [
        t('books.day'),
        t('books.orders'),
        t('books.gross'),
        t('books.cash'),
        t('books.card'),
        t('books.tips'),
        t('books.fiscalReceipts'),
        t('books.missingFiscal'),
        t('books.cashDesk'),
        t('books.cardAccount'),
      ],
      pack.sales.map((r) => [
        r.day,
        r.orders,
        r.gross,
        r.cash,
        r.card,
        r.tips,
        r.fiscal_orders,
        r.missing_fiscal,
        codes.cash_desk_code,
        codes.card_account_code,
      ]),
    ),
    sheet(
      t('books.sheetTax'),
      [
        t('books.day'),
        t('books.departmentNo'),
        t('books.departmentName'),
        t('books.taxType'),
        t('books.net'),
        t('books.discounts'),
        t('books.cogs'),
        t('books.incomeCode'),
      ],
      pack.tax.map((r) => [
        r.day,
        r.department_id,
        r.department_name,
        taxTypeLabel(t, r.tax_type),
        r.net,
        r.discounts,
        r.cogs,
        incomeCode(codes, r.tax_type),
      ]),
    ),
    sheet(
      t('books.sheetOrders'),
      [
        t('books.day'),
        t('books.order'),
        t('books.kind'),
        t('books.fiscalNo'),
        t('books.departmentNo'),
        t('books.departmentName'),
        t('books.taxType'),
        t('books.lineTotal'),
        t('books.discounts'),
        t('books.orderCash'),
        t('books.orderCard'),
        t('books.tips'),
        t('books.tin'),
        t('books.compType'),
        t('books.closeReason'),
      ],
      pack.orderLines.map((r) => [
        r.day,
        r.order_number,
        orderKindLabel(t, r.kind),
        r.fiscal_no,
        r.department_id,
        r.department_name,
        taxTypeLabel(t, r.tax_type),
        r.line_net,
        r.discount,
        r.cash,
        r.card,
        r.tip,
        r.partner_tin,
        r.comp_type,
        r.close_reason,
      ]),
    ),
    sheet(
      t('books.sheetReturns'),
      [t('books.day'), t('books.order'), t('books.amount'), t('books.fiscalNo'), t('books.tin')],
      pack.returns.map((r) => [r.day, r.order_number, r.total, r.fiscal_no, r.partner_tin]),
    ),
    sheet(
      t('books.sheetComps'),
      [t('books.day'), t('books.compType'), t('books.count'), t('books.amount')],
      pack.comps.map((r) => [r.day, r.comp_type, r.count, r.amount]),
    ),
    sheet(
      t('books.sheetVoids'),
      [t('books.day'), t('books.order'), t('books.amount'), t('books.closeReason'), t('books.compType')],
      pack.voids.map((r) => [r.day, r.order_number, r.total, r.close_reason, r.comp_type]),
    ),
    sheet(
      t('books.sheetB2b'),
      [t('books.day'), t('books.order'), t('books.tin'), t('books.amount'), t('books.fiscalNo')],
      pack.b2b.map((r) => [r.day, r.order_number, r.tin, r.total, r.fiscal_no]),
    ),
    sheet(
      t('books.sheetPurchases'),
      [
        t('books.day'),
        t('books.serial'),
        t('books.tin'),
        t('books.supplier'),
        t('books.goods'),
        t('books.qty'),
        t('books.unit'),
        t('books.unitPrice'),
        t('books.lineTotal'),
        t('books.vat'),
        t('books.invoiceTotal'),
        t('books.accountCode'),
      ],
      pack.purchases.map((r) => [
        r.day,
        r.serial_no,
        r.supplier_tin,
        r.supplier_name,
        r.goods_name,
        r.quantity,
        r.unit,
        r.unit_price,
        r.line_total,
        r.invoice_vat,
        r.invoice_total,
        codes.purchase_account_code,
      ]),
    ),
    sheet(
      t('books.sheetStock'),
      [
        t('books.day'),
        t('books.ingredient'),
        t('books.unit'),
        t('books.systemQty'),
        t('books.countedQty'),
        t('books.delta'),
        t('books.unitPrice'),
        t('books.value'),
        t('books.note'),
      ],
      pack.stock.map((r) => [
        r.day,
        r.ingredient,
        r.unit,
        r.system_qty,
        r.counted_qty,
        r.delta,
        r.unit_cost,
        r.value,
        r.note,
      ]),
    ),
    sheet(
      t('books.sheetOnHand'),
      [
        t('books.ingredient'),
        t('books.unit'),
        t('books.qty'),
        t('books.unitPrice'),
        t('books.value'),
      ],
      pack.onHand.map((r) => [r.ingredient, r.unit, r.quantity, r.unit_cost, r.value]),
    ),
    sheet(
      t('books.sheetStays'),
      [
        t('books.checkIn'),
        t('books.checkOut'),
        t('books.room'),
        t('books.guest'),
        t('books.nights'),
        t('books.amount'),
        t('books.paymentOption'),
        t('books.paymentStatus'),
        t('books.tender'),
        t('books.fiscalNo'),
        t('books.source'),
      ],
      pack.stays.map((r) => [
        r.check_in,
        r.check_out,
        r.room,
        r.guest_name,
        r.nights,
        r.amount,
        stayOptionLabel(t, r.payment_option),
        stayStatusLabel(t, r.payment_status),
        stayTenderLabel(t, r.payment_method),
        r.fiscal_no,
        r.source,
      ]),
    ),
    sheet(
      t('books.sheetWaste'),
      [
        t('books.day'),
        t('books.ingredient'),
        t('books.unit'),
        t('books.qty'),
        t('books.unitPrice'),
        t('books.value'),
        t('books.note'),
      ],
      pack.waste.map((r) => [
        r.day,
        r.ingredient,
        r.unit,
        r.quantity,
        r.unit_cost,
        r.value,
        r.note,
      ]),
    ),
    sheet(
      t('books.sheetDrops'),
      [t('books.day'), t('books.expected'), t('books.counted'), t('books.status'), t('books.note'), t('books.cashDesk')],
      pack.drops.map((r) => [
        r.day,
        r.expected_amount,
        r.counted_amount,
        r.status,
        r.note,
        codes.cash_desk_code,
      ]),
    ),
    sheet(
      t('books.sheetExpenses'),
      [
        t('books.day'),
        t('books.category'),
        t('books.amount'),
        t('books.description'),
        t('books.cashDesk'),
      ],
      pack.expenses.map((r) => [
        r.day,
        r.category,
        r.amount,
        r.description,
        codes.cash_desk_code,
      ]),
    ),
  ];

  for (const s of sheets) XLSX.utils.book_append_sheet(book, s.sheet, s.name);
  XLSX.writeFile(book, filename);
}
